import { FileIcon } from "lucide-react";
import { useCallback, useEffect, useRef } from "react";
import { type Accept, useDropzone } from "react-dropzone";

import { DropzoneOverlay } from "@/components/dropzone-overlay";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/components/ui/lib/utils";
import { useUploadFile } from "@/hooks/use-upload-file";

import { useFieldContext } from "./form-context";

function formatFileSize(bytes: number) {
	const units = ["B", "KB", "MB", "GB"];
	let size = bytes;
	let unitIndex = 0;

	while (size >= 1024 && unitIndex < units.length - 1) {
		size /= 1024;
		unitIndex++;
	}

	return `${size.toFixed(2)} ${units[unitIndex]}`;
}

type FileUploadProps = {
	className?: string;
	/**
	 * Accept file types, e.g. { "application/octet-stream": [".bin"] }
	 * @see https://react-dropzone.js.org/#section-accepting-specific-file-types
	 */
	accept?: Accept;
	/**
	 * Accept string for native input, e.g. ".bin"
	 */
	inputAccept?: string;
	/**
	 * Allow multiple file selection
	 */
	multiple?: boolean;
	/**
	 * Description text shown below the upload area
	 */
	description?: string;
};

export function FileUpload({
	className,
	accept,
	inputAccept,
	multiple = false,
	description,
}: FileUploadProps) {
	const field = useFieldContext<string>();
	const value = field.store.state.value;
	const inputRef = useRef<HTMLInputElement>(null);

	const {
		file,
		resetFile,
		mutate: uploadFile,
		isPending,
	} = useUploadFile({
		onSuccess: (result) => {
			if (result.id) {
				field.handleChange(result.id);
			}
		},
	});

	const onDrop = useCallback(
		(acceptedFiles: File[]) => {
			const selectedFile = acceptedFiles[0];
			if (!selectedFile) return;
			uploadFile(selectedFile);
		},
		[uploadFile],
	);

	const { getRootProps, getInputProps, isDragActive } = useDropzone({
		onDrop,
		accept,
		multiple,
		noClick: true,
		noKeyboard: true,
	});

	// Sync local file state with form value
	useEffect(() => {
		if (!value && file) {
			resetFile();
		}
	}, [value, file, resetFile]);

	const handleRemoveFile = () => {
		resetFile();
		field.handleChange("");
		if (inputRef.current) {
			inputRef.current.value = "";
		}
	};

	const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const selectedFile = e.target.files?.[0];
		if (selectedFile) {
			uploadFile(selectedFile);
		}
	};

	const isUploading = file?.status === "uploading" || file?.status === "processing" || isPending;
	const isSuccess = file?.status === "success" && value;

	return (
		<div {...getRootProps()} className={cn("relative space-y-1", className)}>
			{isDragActive && <DropzoneOverlay size="sm" />}
			<input {...getInputProps()} />
			{isSuccess && file ? (
				<div className="flex items-center gap-2 p-3 bg-muted rounded-md">
					<FileIcon className="w-4 h-4 shrink-0" />
					<span className="text-sm flex-1 truncate">
						{file.file.name} ({formatFileSize(file.file.size)})
					</span>
					<Button type="button" variant="ghost" size="sm" onClick={handleRemoveFile}>
						删除
					</Button>
				</div>
			) : (
				<div className="flex gap-2 items-center">
					<Input
						ref={inputRef}
						type="file"
						accept={inputAccept}
						onChange={handleFileChange}
						disabled={isUploading}
						className="flex-1"
					/>
					{isUploading && (
						<span className="text-sm text-muted-foreground whitespace-nowrap">
							{file?.status === "processing"
								? "处理中..."
								: `上传中... ${file?.uploadState?.progress?.toFixed(0) || 0}%`}
						</span>
					)}
				</div>
			)}
			{description && <p className="text-sm text-muted-foreground">{description}</p>}
		</div>
	);
}
